import { apiRequest } from '../api-client';
import { API_ENDPOINTS } from '../api-config';
import { getAuthState } from '@/store/auth-store';

/**
 * Get tenant_id from auth store
 */
const getTenantId = (): string => {
  const authState = getAuthState();
  return authState.user?.tenant_id || '';
};

// Types
export interface UserPreferences {
  preference_id: string;
  user_id: string;
  tenant_id: string;
  favorite_nodes: string[];
  ui_preferences: {
    sidebar_collapsed?: boolean;
    dark_mode?: boolean | null;
    canvas_grid_visible?: boolean;
    minimap_visible?: boolean;
    default_zoom?: number;
  };
  workflow_preferences: {
    auto_save?: boolean;
    auto_save_interval_seconds?: number;
    show_node_descriptions?: boolean;
    snap_to_grid?: boolean;
    grid_size?: number;
  };
  notification_preferences: {
    email_on_workflow_complete?: boolean;
    email_on_workflow_failure?: boolean;
    browser_notifications?: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface RecentNode {
  node_type: string;
  node_category: string | null;
  usage_count: number;
  last_used_at: string;
  is_favorite?: boolean;
}

export interface NodeSidebarData {
  favorites: string[];
  recent_nodes: RecentNode[];
}

export interface UserContext {
  last_workspace_id?: string;
  last_workspace_name?: string;
  last_project_id?: string;
  last_project_name?: string;
  last_embedding_model?: string;
  last_embedding_connector_id?: string;
  last_embedding_connector_name?: string;
  last_llm_model?: string;
  last_llm_connector_id?: string;
  last_llm_connector_name?: string;
  last_vector_db_provider?: string;
  last_chunk_size?: number;
  last_chunk_overlap?: number;
  last_top_k?: number;
  theme?: 'dark' | 'light' | 'system';
  brand_theme?: string; // one of BRAND_THEME_IDS — see brand-theme-store.ts
  show_top_navbar?: boolean; // default false (navbar hidden); user pref in MyProfile → Preferences
}

/**
 * User Preferences Service
 * Handles user preferences API calls including favorites and recently used nodes
 */
export const UserPreferencesService = {
  /**
   * Get user preferences
   */
  async getPreferences(): Promise<UserPreferences> {
    const tenantId = getTenantId();
    const response = await apiRequest<UserPreferences>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.get,
      params: { tenant_id: tenantId },
    });
    return response;
  },

  /**
   * Update user preferences
   */
  async updatePreferences(updates: Partial<UserPreferences>): Promise<UserPreferences> {
    const tenantId = getTenantId();
    const response = await apiRequest<UserPreferences>({
      method: 'PATCH',
      url: API_ENDPOINTS.userPreferences.update,
      params: { tenant_id: tenantId },
      data: updates,
    });
    return response;
  },

  /**
   * Get favorite node types
   */
  async getFavorites(): Promise<{ favorites: string[] }> {
    const tenantId = getTenantId();
    const response = await apiRequest<{ favorites: string[] }>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.favorites,
      params: { tenant_id: tenantId },
    });
    return response;
  },

  /**
   * Toggle favorite status for a node type
   * Returns true if now favorited, false if unfavorited
   */
  async toggleFavorite(nodeType: string): Promise<{ node_type: string; is_favorite: boolean }> {
    const tenantId = getTenantId();
    const response = await apiRequest<{ node_type: string; is_favorite: boolean }>({
      method: 'POST',
      url: API_ENDPOINTS.userPreferences.toggleFavorite(nodeType),
      params: { tenant_id: tenantId },
    });
    return response;
  },

  /**
   * Get recently used nodes
   */
  async getRecentNodes(limit: number = 10): Promise<{ recent_nodes: RecentNode[] }> {
    const tenantId = getTenantId();
    const response = await apiRequest<{ recent_nodes: RecentNode[] }>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.recentNodes,
      params: { tenant_id: tenantId, limit },
    });
    return response;
  },

  /**
   * Record that a node type was used
   */
  async recordNodeUsage(nodeType: string, nodeCategory?: string): Promise<{ status: string; node_type: string }> {
    const tenantId = getTenantId();
    const response = await apiRequest<{ status: string; node_type: string }>({
      method: 'POST',
      url: API_ENDPOINTS.userPreferences.recordUsage(nodeType),
      params: { tenant_id: tenantId, node_category: nodeCategory },
    });
    return response;
  },

  /**
   * Get frequently used nodes
   */
  async getFrequentNodes(limit: number = 10): Promise<{ frequent_nodes: RecentNode[] }> {
    const tenantId = getTenantId();
    const response = await apiRequest<{ frequent_nodes: RecentNode[] }>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.frequentNodes,
      params: { tenant_id: tenantId, limit },
    });
    return response;
  },

  /**
   * Get combined node sidebar data (favorites + recent)
   */
  async getNodeSidebarData(recentLimit: number = 10): Promise<NodeSidebarData> {
    const tenantId = getTenantId();
    const response = await apiRequest<NodeSidebarData>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.nodeSidebar,
      params: { tenant_id: tenantId, recent_limit: recentLimit },
    });
    return response;
  },

  /**
   * Get user context (last workspace/project, model selections, theme).
   *
   * Returns a Promise that rejects with `Error('tenant_not_ready')` when
   * the auth store hasn't hydrated yet (no `tenant_id`). Without this
   * guard the call would fire with `tenant_id=` (blank) and the
   * backend's 401 retry loop produced the 85x repeated request bursts
   * caught by the 2026-05-20 public audit (triage-notes.md, finding #4).
   *
   * Callers should swallow `tenant_not_ready` and retry once auth is
   * known. SWR / React Query layers already handle this pattern.
   */
  async getUserContext(): Promise<UserContext> {
    const tenantId = getTenantId();
    if (!tenantId) {
      throw new Error('tenant_not_ready');
    }
    const response = await apiRequest<UserContext>({
      method: 'GET',
      url: API_ENDPOINTS.userPreferences.context,
      params: { tenant_id: tenantId },
    });
    return response;
  },

  /**
   * Partially update user context (merges with existing). Same
   * tenant-readiness guard as the GET path; nothing useful happens
   * when we don't yet know which tenant to write to.
   */
  async updateUserContext(updates: Partial<UserContext>): Promise<UserContext> {
    const tenantId = getTenantId();
    if (!tenantId) {
      throw new Error('tenant_not_ready');
    }
    const response = await apiRequest<UserContext>({
      method: 'PATCH',
      url: API_ENDPOINTS.userPreferences.context,
      params: { tenant_id: tenantId },
      data: updates,
    });
    return response;
  },
};

export default UserPreferencesService;
